import React, { useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useDataScope } from "@/hooks/useDataScope";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import { BarChart3, Play, Download, FileSpreadsheet, Loader2, Layers, CalendarDays } from "lucide-react";

function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function Reports() {
  const { param, allMode, accountId } = useDataScope();
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ran, setRan] = useState(false);

  const run = async () => {
    if (start > end) return toast.error("Start date must be on or before end date");
    setLoading(true);
    setRan(true);
    try {
      const qs = `start=${start}&end=${end}${param ? `&${param}` : ""}`;
      const { data: d } = await api.get(`/reports/orders?${qs}`);
      setData(d);
    } catch (e) {
      toast.error("Could not run the report");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const download = (format) => {
    const qs = `start=${start}&end=${end}&format=${format}${param ? `&${param}` : ""}`;
    window.open(`${API_BASE}/reports/orders/export?${qs}`, "_blank", "noopener");
  };

  const rows = data?.rows || [];

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
          <BarChart3 className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Reports</h1>
          <p className="text-slate-500 mt-0.5">Pull every order shipped in a date range and export it to CSV or Excel.</p>
        </div>
      </div>

      {allMode ? (
        <div data-testid="reports-all-mode" className="flex items-start gap-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-4 py-4">
          <Layers className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="text-sm">Reports are per store. Pick a specific store from the account switcher (top-right) to run a report.</p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">From (ship date)</label>
                <div className="relative">
                  <CalendarDays className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input data-testid="report-start-date" type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)}
                    className="pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">To (ship date)</label>
                <div className="relative">
                  <CalendarDays className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input data-testid="report-end-date" type="date" value={end} min={start} max={today()} onChange={(e) => setEnd(e.target.value)}
                    className="pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500" />
                </div>
              </div>
              <button data-testid="run-report-btn" onClick={run} disabled={loading}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold px-5 py-2.5 rounded-md transition-colors">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run report
              </button>

              {rows.length > 0 && (
                <div className="flex gap-2 sm:ml-auto">
                  <button data-testid="download-csv-btn" onClick={() => download("csv")}
                    className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold px-4 py-2.5 rounded-md transition-colors">
                    <Download className="w-4 h-4" /> CSV
                  </button>
                  <button data-testid="download-xlsx-btn" onClick={() => download("xlsx")}
                    className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2.5 rounded-md transition-colors">
                    <FileSpreadsheet className="w-4 h-4" /> Excel
                  </button>
                </div>
              )}
            </div>
          </div>

          {data?.totals && rows.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-totals">
              <Kpi label="Shipments" value={rows.length} />
              <Kpi label="Units" value={data.totals.units} />
              <Kpi label="Shipping" value={formatCurrency(data.totals.shipping_cost)} />
              <Kpi label="Total billed" value={formatCurrency(data.totals.total)} accent />
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-slate-400 flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Running report…</div>
            ) : !ran ? (
              <p className="p-12 text-center text-sm text-slate-400">Choose a date range and click <strong>Run report</strong> to pull your orders.</p>
            ) : rows.length === 0 ? (
              <p className="p-12 text-center text-sm text-slate-400">No shipments found in this date range.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm" data-testid="report-results-table">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
                    <tr>
                      <th className="py-2.5 px-3">Order #</th>
                      <th className="py-2.5 px-3">Ship Date</th>
                      <th className="py-2.5 px-3">Customer</th>
                      <th className="py-2.5 px-3">Ship To</th>
                      <th className="py-2.5 px-3">Carrier</th>
                      <th className="py-2.5 px-3">Tracking</th>
                      <th className="py-2.5 px-3 text-right">Units</th>
                      <th className="py-2.5 px-3 text-right">Shipping</th>
                      <th className="py-2.5 px-3 text-right">Fulfillment</th>
                      <th className="py-2.5 px-3 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={`${r.order_number}-${i}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                        <td className="py-2.5 px-3 font-semibold text-slate-900">{r.order_number}</td>
                        <td className="py-2.5 px-3 text-slate-600">{r.ship_date}</td>
                        <td className="py-2.5 px-3 text-slate-700">{r.customer_name}</td>
                        <td className="py-2.5 px-3 text-slate-600">{[r.ship_to_city, r.ship_to_state].filter(Boolean).join(", ")}</td>
                        <td className="py-2.5 px-3 text-slate-600">{r.carrier}</td>
                        <td className="py-2.5 px-3 text-slate-500 font-mono text-xs">{r.tracking_number}</td>
                        <td className="py-2.5 px-3 text-right text-slate-700">{r.units}</td>
                        <td className="py-2.5 px-3 text-right text-slate-700">{formatCurrency(r.shipping_cost)}</td>
                        <td className="py-2.5 px-3 text-right text-slate-700">{formatCurrency(r.fulfillment_cost)}</td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-900">{formatCurrency(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`rounded-xl p-4 border ${accent ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200"}`}>
      <p className={`text-xs ${accent ? "text-blue-100" : "text-slate-500"}`}>{label}</p>
      <p className={`text-2xl font-bold ${accent ? "text-white" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}
